import type { BoardSharing, BoardSummary, LinkAccess } from '@doriavboard/shared';
import { Check, Copy, FolderOpen, Globe2, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../api/client';
import { boardsApi } from '../api/endpoints';
import { toast } from '../stores/toasts';
import { PeopleWithAccess } from './PeopleWithAccess';
import { Button } from './ui/Button';
import { Select } from './ui/Input';
import { Modal } from './ui/Modal';

const ACCESS_TEXT: Record<LinkAccess, string> = {
  none: 'Only people added below can open this board',
  view: 'Anyone with the link can view, no sign-in needed',
  edit: 'Anyone with the link can edit, no sign-in needed',
};

interface ShareDialogProps {
  board: BoardSummary;
  open: boolean;
  onOpenChange(open: boolean): void;
  onBoardChange?(board: BoardSummary): void;
}

export function boardLink(boardId: string): string {
  return `${window.location.origin}/b/${boardId}`;
}

export function ShareDialog({ board, open, onOpenChange, onBoardChange }: ShareDialogProps) {
  const [sharing, setSharing] = useState<BoardSharing | null>(null);
  const [copied, setCopied] = useState(false);
  const isOwner = board.role === 'owner';

  useEffect(() => {
    if (!open) return;
    setSharing(null);
    boardsApi
      .sharing(board.id)
      .then(setSharing)
      .catch((err) => toast(errorMessage(err), 'error'));
  }, [open, board.id]);

  async function invite(username: string, role: 'editor' | 'viewer') {
    setSharing(await boardsApi.invite(board.id, username, role));
    toast('Shared', 'success');
  }

  async function changeRole(userId: string, next: 'editor' | 'viewer') {
    try {
      setSharing(await boardsApi.setMemberRole(board.id, userId, next));
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function remove(userId: string) {
    try {
      await boardsApi.removeMember(board.id, userId);
      setSharing((s) => s && { ...s, members: s.members.filter((m) => m.user.id !== userId) });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function changeAccess(linkAccess: LinkAccess) {
    try {
      const { board: updated } = await boardsApi.update(board.id, { linkAccess });
      setSharing((s) => s && { ...s, linkAccess });
      onBoardChange?.(updated);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(boardLink(board.id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast('Couldn’t copy. Select the address bar and copy the link from there.', 'error');
    }
  }

  const access = sharing?.linkAccess ?? board.linkAccess;

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Share “${board.title}”`} size="lg">
      <PeopleWithAccess
        people={sharing}
        canManage={isOwner}
        onInvite={invite}
        onChangeRole={changeRole}
        onRemove={remove}
      />

      {board.space && (
        <div className="mb-5 flex items-center gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
          <span className="grid size-9 place-items-center rounded-full bg-white text-slate-600 shadow-sm dark:bg-slate-900 dark:text-slate-300">
            <FolderOpen className="size-4" />
          </span>
          <p className="min-w-0 flex-1 text-sm">
            Everyone in the space{' '}
            <Link
              to={`/s/${board.space.id}`}
              className="font-medium text-brand-600 hover:underline dark:text-brand-400"
              onClick={() => onOpenChange(false)}
            >
              {board.space.name}
            </Link>{' '}
            can open this board too.
          </p>
        </div>
      )}

      <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        General access
      </h3>
      <div className="mb-6 flex items-center gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
        <span className="grid size-9 place-items-center rounded-full bg-white text-slate-600 shadow-sm dark:bg-slate-900 dark:text-slate-300">
          {access === 'none' ? <Lock className="size-4" /> : <Globe2 className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          {isOwner ? (
            <Select
              aria-label="Link access"
              className="h-8 border-none bg-transparent px-0 font-medium shadow-none"
              value={access}
              onChange={(e) => changeAccess(e.target.value as LinkAccess)}
            >
              <option value="none">Restricted</option>
              <option value="view">Anyone with the link · Viewer</option>
              <option value="edit">Anyone with the link · Editor</option>
            </Select>
          ) : (
            <p className="text-sm font-medium">
              {access === 'none'
                ? 'Restricted'
                : `Anyone with the link · ${access === 'edit' ? 'Editor' : 'Viewer'}`}
            </p>
          )}
          <p className="text-xs text-slate-500">{ACCESS_TEXT[access]}</p>
        </div>
      </div>

      <div className="flex justify-between gap-2">
        <Button onClick={copyLink}>
          {copied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
          {copied ? 'Link copied' : 'Copy link'}
        </Button>
        <Button variant="primary" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      </div>
    </Modal>
  );
}
